import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { menuPermissionsAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { 
  Menu, 
  Save, 
  RotateCcw, 
  Eye, 
  EyeOff,
  AlertTriangle,
  FlaskConical,
  Shield,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

const MenuPermissions = () => {
  const { isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [permissions, setPermissions] = useState([]);
  const [originalPermissions, setOriginalPermissions] = useState([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  // Load permissions
  useEffect(() => {
    loadPermissions();
  }, []);

  const loadPermissions = async () => {
    try {
      setLoading(true);
      const response = await menuPermissionsAPI.getAll();
      const data = response.data;
      setPermissions(data.permissions || []);
      setOriginalPermissions(JSON.parse(JSON.stringify(data.permissions || [])));
      setLastUpdated(data.updated_at);
      setHasChanges(false);
    } catch (error) {
      console.error('Error loading permissions:', error);
      toast.error('Error al cargar permisos de menú');
    } finally {
      setLoading(false);
    }
  };

  // Check if changes were made
  useEffect(() => {
    const changed = JSON.stringify(permissions) !== JSON.stringify(originalPermissions);
    setHasChanges(changed);
  }, [permissions, originalPermissions]);

  // Update a permission with new simplified model
  const updatePermission = (menuKey, subKey, field, value) => {
    setPermissions(prev => prev.map(perm => {
      if (perm.menu_key === menuKey && perm.sub_key === subKey) {
        const updated = { ...perm, [field]: value };
        
        // Si is_beta se activa, deshabilitar los toggles de viewer/user
        // (es solo visual, el backend ya los ignora cuando is_beta=true)
        
        return updated;
      }
      return perm;
    }));
  };

  // Save changes with new model
  const handleSave = async () => {
    try {
      setSaving(true);
      
      // Build updates array with new simplified model
      const updates = permissions.map(perm => ({
        menu_key: perm.menu_key,
        sub_key: perm.sub_key,
        visible_for_viewer: perm.visible_for_viewer,
        visible_for_user: perm.visible_for_user,
        is_beta: perm.is_beta
      }));
      
      await menuPermissionsAPI.update(updates);
      
      // Refresh data
      await loadPermissions();
      
      toast.success('Permisos guardados correctamente');
    } catch (error) {
      console.error('Error saving permissions:', error);
      const message = error.response?.data?.detail || 'Error al guardar permisos';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  // Reset to defaults
  const handleReset = async () => {
    try {
      setSaving(true);
      await menuPermissionsAPI.reset();
      await loadPermissions();
      toast.success('Permisos restablecidos a valores por defecto');
    } catch (error) {
      console.error('Error resetting permissions:', error);
      toast.error('Error al restablecer permisos');
    } finally {
      setSaving(false);
      setResetConfirmOpen(false);
    }
  };

  // Discard changes
  const handleDiscard = () => {
    setPermissions(JSON.parse(JSON.stringify(originalPermissions)));
    setHasChanges(false);
  };

  // Group permissions by menu_key
  const groupedPermissions = permissions.reduce((acc, perm) => {
    if (!acc[perm.menu_key]) {
      acc[perm.menu_key] = {
        menu_label: perm.menu_label,
        items: []
      };
    }
    acc[perm.menu_key].items.push(perm);
    return acc;
  }, {});

  if (!isAdmin()) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <p className="text-slate-500">No tienes permisos para acceder a esta sección.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-5xl mx-auto space-y-6" data-testid="menu-permissions-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-['Manrope']">
              Gestión de Menús
            </h1>
            <p className="text-slate-500 text-sm mt-1">
              Configura la visibilidad de módulos y menús por rol de usuario
            </p>
          </div>
          
          <div className="flex items-center gap-2">
            {hasChanges && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleDiscard}
                disabled={saving}
              >
                Descartar
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setResetConfirmOpen(true)}
              disabled={saving}
            >
              <RotateCcw className="w-4 h-4 mr-1" />
              Restablecer
            </Button>
            <Button
              onClick={handleSave}
              disabled={!hasChanges || saving}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              <Save className="w-4 h-4 mr-1" />
              {saving ? 'Guardando...' : 'Guardar cambios'}
            </Button>
          </div>
        </div>

        {/* Info Banner */}
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4 flex items-start gap-3">
            <Shield className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-medium">Reglas de visibilidad</p>
              <ul className="mt-2 space-y-1.5 text-blue-700">
                <li className="flex items-center gap-2">
                  <span className="w-4 h-4 bg-emerald-500 rounded-full flex items-center justify-center text-white text-xs">✓</span>
                  <span><strong>Admin</strong> siempre ve TODOS los menús (ignora toggles Viewer/User).</span>
                </li>
                <li className="flex items-center gap-2">
                  <FlaskConical className="w-4 h-4 text-amber-500" />
                  <span>Si <strong>BETA</strong> está activo, solo Admin puede ver el módulo.</span>
                </li>
                <li className="flex items-center gap-2">
                  <Eye className="w-4 h-4 text-slate-500" />
                  <span>Viewer/User ven solo los módulos habilitados para su rol (si no es BETA).</span>
                </li>
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* Permissions Table */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Menu className="w-5 h-5" />
              Permisos por Módulo
            </CardTitle>
            <CardDescription>
              Controla qué módulos son visibles para cada rol
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-6 space-y-4">
                {[1, 2, 3, 4].map(i => (
                  <Skeleton key={i} className="h-16 w-full" />
                ))}
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {/* Header */}
                <div className="grid grid-cols-12 gap-4 px-6 py-3 bg-slate-50 text-xs font-semibold text-slate-500 uppercase">
                  <div className="col-span-5">Módulo / Menú</div>
                  <div className="col-span-2 text-center">Viewer</div>
                  <div className="col-span-2 text-center">User</div>
                  <div className="col-span-3 text-center">
                    <span className="flex items-center justify-center gap-1">
                      <FlaskConical className="w-3 h-3" />
                      Beta
                    </span>
                  </div>
                </div>

                {/* Body */}
                {Object.entries(groupedPermissions).map(([menuKey, group]) => (
                  <div key={menuKey} className="divide-y divide-slate-50">
                    {/* Module Header */}
                    <div className="grid grid-cols-12 gap-4 px-6 py-3 bg-slate-25 items-center">
                      <div className="col-span-5">
                        <span className="font-semibold text-slate-800 flex items-center gap-2">
                          {group.menu_label}
                          {group.items.some(i => i.is_beta) && (
                            <Badge variant="outline" className="bg-amber-50 text-amber-600 border-amber-200 text-xs">
                              BETA
                            </Badge>
                          )}
                        </span>
                      </div>
                      <div className="col-span-7"></div>
                    </div>

                    {/* Submenus */}
                    {group.items.map(perm => {
                      const isBeta = perm.is_beta;
                      
                      return (
                        <div 
                          key={`${perm.menu_key}-${perm.sub_key}`}
                          className={cn(
                            "grid grid-cols-12 gap-4 px-6 py-3 items-center transition-colors",
                            isBeta && "bg-amber-50/50"
                          )}
                        >
                          {/* Menu/Submenu Name */}
                          <div className="col-span-5 flex items-center gap-2 pl-4">
                            {isBeta ? (
                              <FlaskConical className="w-4 h-4 text-amber-500" />
                            ) : perm.visible_for_viewer || perm.visible_for_user ? (
                              <Eye className="w-4 h-4 text-emerald-500" />
                            ) : (
                              <EyeOff className="w-4 h-4 text-slate-400" />
                            )}
                            <span className={cn(
                              "text-sm",
                              isBeta ? "text-amber-700" : "text-slate-700"
                            )}>
                              {perm.sub_label || perm.menu_label}
                            </span>
                            {isBeta && (
                              <span className="text-xs text-amber-600 italic">(Solo Admin)</span>
                            )}
                          </div>

                          {/* Viewer Toggle */}
                          <div className="col-span-2 flex justify-center">
                            <Switch
                              checked={perm.visible_for_viewer}
                              onCheckedChange={(checked) => updatePermission(perm.menu_key, perm.sub_key, 'visible_for_viewer', checked)}
                              disabled={isBeta}
                              className={cn(isBeta && "opacity-50")}
                              data-testid={`toggle-viewer-${perm.menu_key}-${perm.sub_key}`}
                            />
                          </div>

                          {/* User Toggle */}
                          <div className="col-span-2 flex justify-center">
                            <Switch
                              checked={perm.visible_for_user}
                              onCheckedChange={(checked) => updatePermission(perm.menu_key, perm.sub_key, 'visible_for_user', checked)}
                              disabled={isBeta}
                              className={cn(isBeta && "opacity-50")}
                              data-testid={`toggle-user-${perm.menu_key}-${perm.sub_key}`}
                            />
                          </div>

                          {/* Beta Toggle */}
                          <div className="col-span-3 flex justify-center">
                            <Switch
                              checked={perm.is_beta}
                              onCheckedChange={(checked) => updatePermission(perm.menu_key, perm.sub_key, 'is_beta', checked)}
                              className={cn(
                                isBeta && "data-[state=checked]:bg-amber-500"
                              )}
                              data-testid={`toggle-beta-${perm.menu_key}-${perm.sub_key}`}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Legend */}
        <Card className="bg-slate-50 border-slate-200">
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-6 text-sm">
              <div className="flex items-center gap-2">
                <Eye className="w-4 h-4 text-emerald-500" />
                <span className="text-slate-600">Visible para el rol</span>
              </div>
              <div className="flex items-center gap-2">
                <EyeOff className="w-4 h-4 text-slate-400" />
                <span className="text-slate-600">Oculto para el rol</span>
              </div>
              <div className="flex items-center gap-2">
                <FlaskConical className="w-4 h-4 text-amber-500" />
                <span className="text-slate-600">En desarrollo (solo Admin)</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Status Info */}
        {lastUpdated && (
          <p className="text-xs text-slate-400 text-center">
            Última actualización: {new Date(lastUpdated).toLocaleString('es-MX')}
          </p>
        )}
      </div>

      {/* Reset Confirmation Dialog */}
      <AlertDialog open={resetConfirmOpen} onOpenChange={setResetConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Restablecer permisos
            </AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción restablecerá todos los permisos de menú a los valores por defecto:
              <ul className="mt-2 space-y-1 list-disc list-inside text-slate-600">
                <li>"Mesa de Ayuda" quedará como BETA (solo visible para Admin)</li>
                <li>"Configuración" solo visible para Admin</li>
                <li>KPIs y Dashboard visibles para todos los roles</li>
              </ul>
              <br />
              ¿Estás seguro de continuar?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleReset} className="bg-amber-600 hover:bg-amber-700">
              Restablecer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
};

export default MenuPermissions;
